/* eslint-disable @typescript-eslint/no-explicit-any */
import { getLocalStorageItem } from '@Builder/QuoteDetailPage/logic'
import { SEE_CHECKOUT } from '@Builder/QuotesHomePage/QuoteCheckoutSummary/components/PricingSections/constants'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FormattedAddress } from '@Builder/QuoteCheckoutPage/types'

import { getPriceDisplay } from '~/lib/getPriceDisplay'
import {
    SHIPPING_OPTION_NAME_MAPPING_UPDATED,
    SHORT_SHIPPING_OPTION_NAME_MAPPING,
} from '~/lib/partsConstants'
import { FictivStateType } from '~/redux-modules/fictivStateType'
import { getUserIdentity } from '~/redux-modules/identity'
import { ShippingOptionType } from '~/redux-modules/quote-detail/types'
import { isTargetDeliveryDateNotMetState } from '~/redux-modules/quote-qllt/selectors'
import {
    CarrierAccountFragment,
    ShippingAddressesItemFragment,
} from '~/gql/graphql'

import {
    CARRIER,
    SELECTED_SHIPPING_ADDRESS_ID_PREFIX,
    SELECTED_SHIPPING_OPTION_PREFIX,
} from './constants'

export const getCarrierAccountNumber = (accountNumber = '') =>
    accountNumber.length > 4
        ? accountNumber.slice(accountNumber.length - 4)
        : accountNumber

export const getCarrierAccountDisplay = (
    carrierAccount?: CarrierAccountFragment | ShippingOptionType,
) =>
    carrierAccount
        ? `${carrierAccount?.provider} - ${getCarrierAccountNumber(
              carrierAccount?.accountNumber,
          )}`
        : ''

export const getMatchShippingOption = (shippingOpt = '') =>
    shippingOpt.split('-')?.[2] || ''

export const getShippingOptionValue = ({
    shippingOption,
    defaultCarrier,
}: {
    shippingOption?: ShippingOptionType
    defaultCarrier?: CarrierAccountFragment
}) => {
    if (shippingOption?.isCarrierOption && defaultCarrier) {
        return `${getCarrierAccountDisplay(shippingOption)} - ${
            SHORT_SHIPPING_OPTION_NAME_MAPPING[
                getMatchShippingOption(shippingOption.id)
            ]
        }`
    }
    const id = shippingOption?.id || ''
    const shippingOptId =
        shippingOption?.isCarrierOption && id?.startsWith(CARRIER)
            ? id.split('-')[1]
            : id

    return (
        SHIPPING_OPTION_NAME_MAPPING_UPDATED[shippingOptId] ||
        shippingOption?.name
    )
}

export const formatAddressInput = (
    address?: ShippingAddressesItemFragment | null,
): FormattedAddress | null => {
    if (!address) {
        return null
    }

    const {
        id,
        name,
        street,
        aptNumber,
        city,
        state,
        zip,
        company,
        country,
        phone,
        timezone,
        isVerified,
    } = address

    return {
        id,
        isVerified,
        name,
        firstName: name,
        address1: street,
        address2: aptNumber,
        city,
        state,
        zip,
        company,
        country,
        phone,
        timezone,
    }
}

export const getShippingAddress = ({ state }: { state: FictivStateType }) => {
    const identity = getUserIdentity(state)
    const userids = state.userDetails && Object.keys(state.userDetails)
    const shippingAddresses =
        state.userDetails?.[userids[0]]?.shippingAddresses ||
        state.userDetails?.[identity?.id]?.shippingAddresses ||
        []

    return shippingAddresses
}

export const getSelectedShippingAddress = (
    state: FictivStateType,
    quoteId: string | undefined,
) => {
    if (!quoteId) {
        return
    }
    const shippingAddresses = getShippingAddress({ state })

    const selectedShippingAddressId =
        Number(
            getLocalStorageItem(
                `${SELECTED_SHIPPING_ADDRESS_ID_PREFIX}${quoteId}`,
            ),
        ) ||
        state?.quoteDetail?.quoteId?.selectedShippingAddressId ||
        ''

    const selectedShippingAddress =
        shippingAddresses?.find(
            shippingAddress =>
                shippingAddress?.id === selectedShippingAddressId,
        ) ||
        shippingAddresses?.find(
            shippingAddress => shippingAddress?.isDefault,
        ) ||
        shippingAddresses[0]

    return formatAddressInput(selectedShippingAddress)
}
export const useGetSelectedShippingAddress = ({
    quoteId,
}: {
    quoteId: string | undefined
}) =>
    useSelector((state: FictivStateType) =>
        getSelectedShippingAddress(state, quoteId),
    )
export const useGetSelectedShippingOptionAndIsFreightShipping = ({
    quoteId,
}: {
    quoteId: string
}) =>
    useSelector((state: FictivStateType) => ({
        isTargetDeliveryDateNotMet: isTargetDeliveryDateNotMetState(state),
        isOverseasShipping:
            state?.quoteDetail?.[quoteId]?.selectedShippingOption?.id?.includes(
                'overseas',
            ) || false,

        selectedShippingOption:
            state?.quoteDetail?.[quoteId]?.selectedShippingOption || null,
        isFreightShipping:
            state?.quoteDetail?.[quoteId]?.isFreightShipping || false,
        selectedAddressId:
            state?.quoteDetail?.[quoteId]?.selectedShippingAddressId ||
            Number(
                getLocalStorageItem(
                    `${SELECTED_SHIPPING_ADDRESS_ID_PREFIX}${quoteId}`,
                ),
            ),
    }))

export const displayShippingCost = ({
    isFreightShipping,
    shippingCost,
}: {
    isFreightShipping?: boolean
    shippingCost?: number | null
}) => {
    return isFreightShipping ? SEE_CHECKOUT : getPriceDisplay(shippingCost)
}

export const getSelectedShippingOption = <T extends { id: string }>({
    shippingOptions = [],
    selectedShippingOptionId,
}: {
    shippingOptions: T[]
    selectedShippingOptionId: string
}) =>
    shippingOptions.find(
        shippingOpt => shippingOpt.id === selectedShippingOptionId,
    ) ||
    shippingOptions?.[0] ||
    null

export const useGetDefaultShippingOption = ({
    prepaidShippingOptions,
    carrierShippingOptions,
    quoteId,
    hasCarrierAccount,
}: {
    quoteId: string
    prepaidShippingOptions: ShippingOptionType[]
    carrierShippingOptions: ShippingOptionType[]
    hasCarrierAccount: boolean
}) =>
    useMemo(() => {
        const selectedShippingOptionId =
            getLocalStorageItem(
                `${SELECTED_SHIPPING_OPTION_PREFIX}${quoteId}`,
            ) || ''

        const selectedShippingOption = getSelectedShippingOption({
            selectedShippingOptionId,
            shippingOptions: [
                ...prepaidShippingOptions,
                ...carrierShippingOptions,
            ],
        })

        return (
            selectedShippingOption ||
            (hasCarrierAccount
                ? carrierShippingOptions[0]
                : prepaidShippingOptions[0])
        )
    }, [
        carrierShippingOptions,
        hasCarrierAccount,
        prepaidShippingOptions,
        quoteId,
    ])

export const shouldUpdateSelectedShippingOption = ({
    selectedShippingOption,
    defaultShippingOption,
}: {
    selectedShippingOption?: ShippingOptionType | null
    defaultShippingOption?: ShippingOptionType
}) => {
    return (
        selectedShippingOption?.quoteToken !== defaultShippingOption?.quoteToken
    )
}

export const checkDeliveryDays = <T extends { deliveryDays?: number | null }>(
    prepaidShippingOptions: T[],
    selectedShippingOption: T | null,
) => {
    if (!selectedShippingOption || !prepaidShippingOptions) {
        return false
    } else {
        const foundOption = prepaidShippingOptions.find(shippingOption => {
            if (
                shippingOption.deliveryDays === undefined ||
                selectedShippingOption.deliveryDays === undefined ||
                shippingOption.deliveryDays === null ||
                selectedShippingOption.deliveryDays === null
            ) {
                return false
            }
            return (
                shippingOption.deliveryDays <
                selectedShippingOption.deliveryDays
            )
        })

        return !!foundOption
    }
}
